import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["readonly"]
const _hoisted_2 = {
  class: "btn-3 right-icon dropdown-btn",
  "data-testing": "file-menu"
}
const _hoisted_3 = {
  key: 0,
  class: "margin-b-half",
  "data-testing": "file-path-sb"
}
const _hoisted_4 = ["href"]
const _hoisted_5 = {
  class: "margin-b-half",
  "data-testing": "files-attached-sb"
}
const _hoisted_6 = { class: "pl-0" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = {
  key: 1,
  class: "margin-b-half"
}
const _hoisted_9 = {
  class: "versions margin-b-half",
  "data-testing": "versions-container"
}
const _hoisted_10 = {
  class: "date",
  "data-testing": "blackout"
}
const _hoisted_11 = { key: 0 }
const _hoisted_12 = ["href"]
const _hoisted_13 = {
  key: 2,
  class: "margin-b-half"
}
const _hoisted_14 = ["disabled"]

import { mdiContentCopy, mdiDelete, mdiDotsHorizontal } from "@mdi/js";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import { PRODUCT } from "@/constants/product.constants";
import { copyToClipboard } from "@/services/copy-to-clipboard";
import { showDialog } from "@/services/notification-manager";
import { useFilesStore } from "@/stores/files.store";
import { useProductsStore } from "@/stores/products.store";
import { useUIFilesStore } from "@/stores/ui-files.store";
import { useUIGeneralStore } from "@/stores/ui-general.store";
import { useUsersStore } from "@/stores/users.store";

import {
  LibraryFileProduct,
  LibraryFileSection,
  LibraryFileVersion
} from "@/models/files.model";
import { ProductItem, ProductPermission } from "@/models/product.model";
import { OrganizationUser } from "@/models/user.model";

import { useFileManager } from "@/composables/files-manager";

import { isAValidPathToTheLocalFile, isSafeUrl } from "@/utils/file-helpers";
import { formatFine } from "@/utils/formatTimestamp";

import RightSidebar from "@/components/right-sidebars/ui/RightSidebar.vue";
import RightSidebarTitle from "@/components/right-sidebars/ui/RightSidebarTitle.vue";

interface Version {
  displayDateTime: string;
  userDisplayName: string;
  location: string;
}

interface Attachment {
  name: string;
  index: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FileSB',
  setup(__props) {

const uiGeneralStore = useUIGeneralStore();
const uiFilesStore = useUIFilesStore();
const usersStore = useUsersStore();
const productsStore = useProductsStore();
const filesStore = useFilesStore();

const fileLocationIsLocal = computed<boolean>(() => {
  return uiFilesStore.fileForDisplay
    ? isAValidPathToTheLocalFile(uiFilesStore.fileForDisplay.location)
    : false;
});
const fileLocationTitle = computed<string>(() => {
  return fileLocationIsLocal.value ? "Path" : "Location";
});

const section = computed<LibraryFileSection | undefined>(() => {
  if (!uiFilesStore.fileForDisplay) {
    return undefined;
  }
  return uiFilesStore.fileForDisplay.sections.find((e: LibraryFileSection) => {
    return (
      !!uiFilesStore.fileForDisplay &&
      e.file_id === uiFilesStore.fileForDisplay.id
    );
  });
});
const productIds = computed<number[]>(() => {
  let ids: Set<number> = new Set([]);
  if (uiFilesStore.fileForDisplay) {
    const products = uiFilesStore.fileForDisplay.products.map(
      (product: LibraryFileProduct) => {
        return product.product_id;
      }
    );
    const productsFromSections = uiFilesStore.fileForDisplay.sections.map(
      (section: LibraryFileSection) => {
        return section.product_id;
      }
    );
    ids = new Set([...products, ...productsFromSections]);
  }
  return Array.from(ids);
});

const getProductName = (productId: number): string => {
  const product = productsStore.productDetails?.find((e: ProductItem) => {
    return e.id === productId;
  });
  return product ? product.name : "";
};
const attachments = computed<Attachment[]>(() => {
  if (!uiFilesStore.fileForDisplay) {
    return [];
  }
  const sections = uiFilesStore.fileForDisplay.sections
    .map((section: LibraryFileSection) => {
      const productName = getProductName(section.product_id);
      return {
        name: productName ? `${productName}/${section.name}` : "",
        index: 0
      };
    })
    .filter((section: { name: string }) => {
      return section.name !== "";
    });
  const products = uiFilesStore.fileForDisplay.products
    .map((product: LibraryFileProduct) => {
      return {
        name: getProductName(product.product_id) || "",
        index: 0
      };
    })
    .filter((product: { name: string }) => {
      return product.name !== "";
    });
  const attachments: Attachment[] = sections.concat(products);
  attachments.forEach((attachment: Attachment, index: number) => {
    attachment.index = index;
  });
  return attachments;
});

const getUserDisplayName = (userId: number): string => {
  const user = usersStore.users?.find(
    (e: OrganizationUser): boolean => e.id == userId
  );
  return user ? user.displayName : "";
};
const versions = computed<Version[]>(() => {
  if (!uiFilesStore.fileForDisplay) {
    return [];
  }
  const versionsWithFileUpdate: LibraryFileVersion[] =
    uiFilesStore.fileForDisplay.versions.filter(
      (v: LibraryFileVersion, i: number, a: LibraryFileVersion[]) => {
        if (i === 0) {
          return true;
        }
        return a[i].location !== a[i - 1].location;
      }
    );

  return versionsWithFileUpdate.map((version: LibraryFileVersion) => {
    return {
      displayDateTime: formatFine(version.created_on),
      userDisplayName: getUserDisplayName(version.created_by),
      location: version.location
    };
  });
});

const canRemoveFile = computed<boolean>(() => {
  if (productsStore.productDetails) {
    const products = productsStore.productDetails.filter(
      (product: ProductItem) => {
        return productIds.value.some((prodId: number) => {
          return prodId === product.id;
        });
      }
    );
    return products.every((product: ProductItem) => {
      return product.allocated_permissions.some((perm: ProductPermission) => {
        return perm.id === PRODUCT.PERMISSIONS.editContentStructure;
      });
    });
  }
  return false;
});

const { renameAttachment, uploadFileVersion, removeFile } = useFileManager();
const name = ref<string>("");
onMounted(() => {
  const file = uiFilesStore.fileForDisplay;
  if (file) {
    name.value = file.name;
  }
});
uiFilesStore.$subscribe((mutation, state) => {
  const newVal = state.fileForDisplay;
  if (newVal) {
    name.value = newVal.name;
  }
});
const updateName = (): void => {
  if (
    uiFilesStore.fileForDisplay &&
    uiFilesStore.fileForDisplay.name !== name.value
  ) {
    renameAttachment(
      {
        id: uiFilesStore.fileForDisplay.id,
        name: name.value
      },
      uiFilesStore.fileForDisplay
    );
  }
};

let timeoutId = ref<number | null>(null);
const clearExistingTimeout = () => {
  if (timeoutId.value !== null) {
    clearTimeout(timeoutId.value);
    timeoutId.value = null;
  }
};
onBeforeUnmount(() => {
  clearExistingTimeout();
});
const newVersion = ref<boolean>(false);
const fileToUpload = ref<File | undefined>(undefined);
const newVersionInput = ref<HTMLInputElement | null>(null);
const uploadNewVersionBtn = ref<HTMLButtonElement | null>(null);
const prepareNewVersion = (): void => {
  newVersion.value = true;
  if (newVersionInput.value && newVersionInput.value.files) {
    fileToUpload.value = newVersionInput.value.files[0];
    timeoutId.value = window.setTimeout(() => {
      if (uploadNewVersionBtn.value) {
        uploadNewVersionBtn.value.focus();
      }
    }, 100);
  }
};
const uploadNewVersion = (): void => {
  newVersion.value = false;
  if (
    uiFilesStore.fileForDisplay &&
    !uiFilesStore.fileForDisplay.is_external &&
    fileToUpload.value
  ) {
    const formData = new FormData();
    formData.append("file", fileToUpload.value);
    formData.append("id", `${uiFilesStore.fileForDisplay.id}`);
    if (section.value) {
      uploadFileVersion({
        formData: formData,
        productId: section.value.product_id
      });
    } else {
      uploadFileVersion({
        formData: formData
      });
    }
    if (newVersionInput.value) {
      newVersionInput.value.value = "";
    }
  } else {
    console.log("update path for external file");
  }
};

const confirmRemoval = (): void => {
  showDialog({
    message: "Are you sure you want to continue?",
    cancelText: "No",
    confirmText: "Yes",
    onConfirm: () => {
      if (uiFilesStore.fileForDisplay) {
        removeFile(uiFilesStore.fileForDisplay).then(() => {
          uiFilesStore.closeFileSidebar();
        });
      }
    }
  });
};

return (_ctx: any,_cache: any) => {
  const _component_SVGIcon = _resolveComponent("SVGIcon")!
  const _component_b_dropdown_item = _resolveComponent("b-dropdown-item")!
  const _component_b_dropdown = _resolveComponent("b-dropdown")!

  return (_openBlock(), _createBlock(RightSidebar, {
    onCloseSidebar: _unref(uiFilesStore).closeFileSidebar,
    "data-testing": 'display-file-sidebar',
    "close-btn-data-testing": 'close-file-sb',
    "is-close-btn-visible": true,
    "is-updating": _unref(filesStore).areFilesUpdating,
    "css-classes": ['display-file-sidebar']
  }, {
    title: _withCtx(() => [
      _withDirectives(_createElementVNode("input", {
        class: "ghost-input",
        "data-testing": "edit-file-name",
        readonly: !_unref(uiGeneralStore).isEditModeEnabled,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((name).value = $event)),
        onChange: updateName
      }, null, 40, _hoisted_1), [
        [_vModelText, name.value]
      ])
    ]),
    dropdown: _withCtx(() => [
      (_unref(uiGeneralStore).isEditModeEnabled && canRemoveFile.value)
        ? (_openBlock(), _createBlock(_component_b_dropdown, {
            key: 0,
            class: "is-block sidebar-dropdown-menu",
            "aria-role": "menu",
            position: "is-bottom-left",
            hoverable: "",
            "mobile-modal": false
          }, {
            trigger: _withCtx(() => [
              _createElementVNode("button", _hoisted_2, [
                _createVNode(_component_SVGIcon, {
                  path: _unref(mdiDotsHorizontal),
                  size: 32
                }, null, 8, ["path"])
              ])
            ]),
            default: _withCtx(() => [
              _createElementVNode("span", null, [
                _createVNode(_component_b_dropdown_item, {
                  "aria-role": "menu-item",
                  focusable: false,
                  custom: true
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("button", {
                      class: "btn-unstyled btn-3 full-width",
                      type: "button",
                      "data-testing": "delete-file-btn",
                      onClick: confirmRemoval
                    }, [
                      _createVNode(_component_SVGIcon, { path: _unref(mdiDelete) }, null, 8, ["path"]),
                      _cache[2] || (_cache[2] = _createTextVNode(" Delete Attachment ", -1))
                    ])
                  ]),
                  _: 1
                })
              ])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    body: _withCtx(() => [
      (_unref(uiFilesStore).fileForDisplay.is_external)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(RightSidebarTitle, {
              title: fileLocationTitle.value + ':'
            }, null, 8, ["title"]),
            (!fileLocationIsLocal.value)
              ? (_openBlock(), _createElementBlock("a", {
                  key: 0,
                  class: "location-link",
                  "data-testing": "location-link",
                  href: 
            _unref(isSafeUrl)(_unref(uiFilesStore).fileForDisplay.location)
              ? _unref(uiFilesStore).fileForDisplay.location
              : undefined
          ,
                  target: "_blank"
                }, _toDisplayString(_unref(uiFilesStore).fileForDisplay.location), 9, _hoisted_4))
              : _createCommentVNode("", true),
            (fileLocationIsLocal.value)
              ? (_openBlock(), _createElementBlock("a", {
                  key: 1,
                  title: "Copy path",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(copyToClipboard)(_unref(uiFilesStore).fileForDisplay.location)))
                }, [
                  _createVNode(_component_SVGIcon, { path: _unref(mdiContentCopy) }, null, 8, ["path"]),
                  _createTextVNode(" " + _toDisplayString(_unref(uiFilesStore).fileForDisplay.location), 1)
                ]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(RightSidebarTitle, { title: 'Attached to:' }),
        _createElementVNode("ul", _hoisted_6, [
          (attachments.value.length === 0)
            ? (_openBlock(), _createElementBlock("li", _hoisted_7, " Not attached to any content"))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(attachments.value, (attachment) => {
            return (_openBlock(), _createElementBlock("li", {
              key: attachment.index,
              "data-testing": "attached-to"
            }, _toDisplayString(attachment.name), 1))
          }), 128))
        ])
      ]),
      (!_unref(uiFilesStore).fileForDisplay.is_external)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(RightSidebarTitle, { title: 'Versions:' }),
            _createElementVNode("ul", _hoisted_9, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(versions.value, (version) => {
                return (_openBlock(), _createElementBlock("li", {
                  class: "version",
                  "data-testing": "version",
                  key: version.id
                }, [
                  _createElementVNode("small", _hoisted_10, _toDisplayString(version.displayDateTime), 1),
                  (version.userDisplayName)
                    ? (_openBlock(), _createElementBlock("small", _hoisted_11, _toDisplayString(version.userDisplayName) + ": ", 1))
                    : _createCommentVNode("", true),
                  (_unref(isSafeUrl)(version.location))
                    ? (_openBlock(), _createElementBlock("a", {
                        key: 1,
                        href: version.location,
                        target: "_blank"
                      }, "view", 8, _hoisted_12))
                    : _createCommentVNode("", true)
                ]))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true),
      (!_unref(uiFilesStore).fileForDisplay.is_external)
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
            _createVNode(RightSidebarTitle, { title: 'Upload a new Version:' }),
            _createElementVNode("form", {
              onSubmit: _withModifiers(uploadNewVersion, ["prevent"])
            }, [
              _createElementVNode("input", {
                class: "margin-b-quarter",
                type: "file",
                ref_key: "newVersionInput",
                ref: newVersionInput,
                onChange: prepareNewVersion,
                disabled: _unref(filesStore).areFilesUpdating
              }, null, 40, _hoisted_14),
              (newVersion.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    class: "btn-1 full-width",
                    ref_key: "uploadNewVersionBtn",
                    ref: uploadNewVersionBtn,
                    onClick: uploadNewVersion
                  }, " Submit ", 512))
                : _createCommentVNode("", true)
            ], 32)
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["onCloseSidebar", "is-updating"]))
}
}

})